// src/services/cloudflareAuthService.ts
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignupData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: 'customer' | 'provider' | 'admin' | 'support' | 'company';
  phone?: string;
  businessName?: string;
  providerType?: 'individual' | 'company';
}

export class CloudflareAuthService {
  private baseUrl = window.location.origin;
  private refreshPromise: Promise<any> | null = null;

  async refreshToken(): Promise<{
    success: boolean;
    token?: string;
    error?: string;
  }> {
    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();
    const result = await this.refreshPromise;
    this.refreshPromise = null;
    return result;
  }

  private async performTokenRefresh(): Promise<{
    success: boolean;
    token?: string;
    error?: string;
  }> {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        return { success: false, error: 'No refresh token available' };
      }

      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Clear invalid tokens
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        return { success: false, error: data.error || 'Token refresh failed' };
      }

      if (data.session?.access_token) {
        localStorage.setItem('auth_token', data.session.access_token);
        localStorage.setItem('refresh_token', data.session.refresh_token);
        return { success: true, token: data.session.access_token };
      }

      return { success: false, error: 'Invalid refresh response' };
    } catch (error) {
      return { success: false, error: 'Network error during token refresh' };
    }
  }

  // Helper method to make authenticated requests with automatic token refresh
  async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    const token = localStorage.getItem('auth_token');
    
    if (!token) {
      throw new Error('No authentication token');
    }

    // Add auth header
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
    };

    let response = await fetch(url, { ...options, headers });

    // If token expired, try to refresh and retry
    if (response.status === 401) {
      const refreshResult = await this.refreshToken();
      
      if (refreshResult.success && refreshResult.token) {
        // Retry with new token
        headers['Authorization'] = `Bearer ${refreshResult.token}`;
        response = await fetch(url, { ...options, headers });
      } else {
        // Refresh failed, redirect to login
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }
    }

    return response;
  }

  async login(email: string, password: string): Promise<{
    success: boolean;
    user?: any;
    token?: string;
    error?: string;
  }> {
    try {
      console.log('🔐 CloudflareAuthService: Attempting login for', email);
      
      const response = await fetch(`${this.baseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      console.log('📡 Login response status:', response.status, response.statusText);

      let data;
      try {
        data = await response.json();
        console.log('📄 Login response data:', { success: data.success, hasUser: !!data.user, hasSession: !!data.session });
      } catch (parseError) {
        console.error('❌ Failed to parse login response as JSON:', parseError);
        return { success: false, error: 'Invalid server response format' };
      }

      if (!response.ok) {
        console.error('❌ Login failed with status:', response.status, 'Error:', data.error);
        return { success: false, error: data.error || `Login failed (${response.status})` };
      }

      if (data.session?.access_token) {
        // Store token for API calls
        localStorage.setItem('auth_token', data.session.access_token);
        localStorage.setItem('refresh_token', data.session.refresh_token);
        console.log('✅ Tokens stored successfully');
      } else {
        console.warn('⚠️ No access token in response');
      }

      console.log('✅ Login successful');
      return {
        success: true,
        user: data.user,
        token: data.session?.access_token,
      };
    } catch (error) {
      console.error('💥 Network error during login:', error);
      return { success: false, error: 'Network error - please check your connection' };
    }
  }

  async signup(userData: SignupData): Promise<{
    success: boolean;
    user?: any;
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Signup failed' };
      }

      return {
        success: true,
        user: data.user,
      };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  async getCurrentUser(): Promise<any> {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No auth token');
      }

      const response = await fetch(`${this.baseUrl}/auth/session`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        // Only clear tokens on 401 (unauthorized) errors, not on network errors
        if (response.status === 401) {
          console.log('🔑 Token expired or invalid, clearing auth data');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
        }
        throw new Error(data.error || 'Failed to get user');
      }

      return data.user;
    } catch (error) {
      // Only clear tokens if it's an auth-related error, not network errors
      if (error instanceof Error && (error.message.includes('401') || error.message.includes('Invalid token'))) {
        console.log('🔑 Auth error, clearing tokens');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
      }
      throw error;
    }
  }

  async logout(): Promise<{ success: boolean; error?: string }> {
    try {
      const token = localStorage.getItem('auth_token');
      
      if (token) {
        await fetch(`${this.baseUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }

      // Clear local storage regardless of API call result
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');

      return { success: true };
    } catch (error) {
      // Still clear local storage even if API call fails
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      return { success: true };
    }
  }

  async loginWithMagicLink(email: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/magic-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Magic link failed' };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  async resetPassword(email: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Password reset failed' };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  async updatePassword(password: string, accessToken: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/update-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password, access_token: accessToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Password update failed' };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  async verifyEmailOTP(email: string, token: string): Promise<{
    success: boolean;
    user?: any;
    session?: any;
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, token, type: 'email' }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'OTP verification failed' };
      }

      // Store tokens if verification successful
      if (data.session?.access_token) {
        localStorage.setItem('auth_token', data.session.access_token);
        localStorage.setItem('refresh_token', data.session.refresh_token);
      }

      return {
        success: true,
        user: data.user,
        session: data.session,
      };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  async resendEmailOTP(email: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/resend-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, type: 'email' }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Failed to resend OTP' };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  async signInWithOAuth(provider: 'google' | 'apple'): Promise<{
    success: boolean;
    url?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/oauth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ provider }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'OAuth failed' };
      }

      return { success: true, url: data.url };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }
}

export const cloudflareAuthService = new CloudflareAuthService();